// Main dashboard: headline numbers, warnings, weekly forecast, booking deadlines.
import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { Spinner, ErrorBox, StatusChip } from '../ui';
import { toAus } from '../util';

export default function Dashboard() {
  const [data, setData] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api('/dashboard').then(setData).catch(e => setError(e.message));
  }, []);

  if (error) return <ErrorBox error={error} />;
  if (!data) return <Spinner label="Loading dashboard…" />;

  const cards: [string, number, boolean?][] = [
    ['Onsite now', data.onsite_now],
    ['Arriving today', data.arriving_today],
    ['Departing today', data.departing_today],
    ['Arriving next 7 days', data.arriving_7_days],
    ['Departing next 7 days', data.departing_7_days],
    ['Rooms required tonight', data.rooms_required_tonight],
    ['Rooms booked tonight', data.rooms_booked_tonight],
    [data.tonight_shortfall > 0 ? 'Shortage tonight' : 'Surplus tonight',
      data.tonight_shortfall > 0 ? data.tonight_shortfall : data.tonight_surplus,
      data.tonight_shortfall > 0]
  ];

  const maxWeek = Math.max(1, ...data.weekly_forecast.map((w: any) => w.peak_onsite));

  return (
    <div className="page">
      <div className="page-head">
        <h2>Dashboard</h2>
        <span className="muted">Site date (Darwin): {toAus(data.date)}</span>
      </div>

      <div className="cards">
        {cards.map(([label, value, bad]) => (
          <div key={label} className={'card' + (bad ? ' card-bad' : '')}>
            <div className="card-value">{value}</div>
            <div className="card-label">{label}</div>
          </div>
        ))}
      </div>

      {data.warnings.length > 0 && (
        <div className="panel">
          <h3>⚠ Warnings ({data.warnings.length})</h3>
          <div className="warning-list">
            {data.warnings.map((w: any, i: number) => (
              <div key={i} className={`warning-item sev-${w.severity}`}>{w.message}</div>
            ))}
          </div>
        </div>
      )}

      <div className="panel">
        <h3>Forecast people onsite by week (peak, next 26 weeks)</h3>
        <div className="chart-wrap">
          <div className="bar-chart" style={{ height: 140 }}>
            {data.weekly_forecast.map((w: any) => (
              <div key={w.week_starting} className="bar-col" title={`Week of ${toAus(w.week_starting)}: ${w.peak_onsite} people`}>
                <div style={{ height: `${(w.peak_onsite / maxWeek) * 100}%`, background: 'var(--olive)', borderRadius: '2px 2px 0 0', minHeight: w.peak_onsite ? 2 : 0 }} />
              </div>
            ))}
          </div>
          <div className="muted">From {toAus(data.weekly_forecast[0]?.week_starting)} — hover bars for detail. <a href="#/demand">Full demand chart →</a></div>
        </div>
      </div>

      <div className="two-col">
        <div className="panel">
          <h3>Upcoming booking actions</h3>
          {data.booking_tasks.length === 0 && <p className="muted">No booking shortfalls in the next six months.</p>}
          {data.booking_tasks.map((t: any, i: number) => (
            <div key={i} className={'warning-item' + (t.overdue ? ' sev-high' : '')} style={{ marginBottom: 6 }}>
              Book <strong>{t.rooms_to_book} room(s)</strong> for {toAus(t.check_in)} – {toAus(t.check_out)} by <strong>{toAusEmphasis(t.latest_safe_booking_date)}</strong>
              {t.overdue ? ' (overdue)' : ''}
            </div>
          ))}
          <a href="#/demand">Open booking planner →</a>
        </div>

        <div className="panel">
          <h3>Needs attention</h3>
          <h4 style={{ fontSize: 12 }}>Unconfirmed personnel ({data.unconfirmed.length})</h4>
          {data.unconfirmed.slice(0, 6).map((u: any) => (
            <div key={u.id} style={{ fontSize: 13, marginBottom: 3 }}>
              {u.name} — {toAus(u.check_in)} to {toAus(u.check_out)} <StatusChip status={u.status} />
            </div>
          ))}
          {data.unconfirmed.length === 0 && <p className="muted">None — everything is confirmed.</p>}
          <h4 style={{ fontSize: 12, marginTop: 12 }}>No accommodation assigned ({data.no_accommodation.length})</h4>
          {data.no_accommodation.slice(0, 6).map((u: any) => (
            <div key={u.id} style={{ fontSize: 13, marginBottom: 3 }}>{u.name} — {toAus(u.check_in)} to {toAus(u.check_out)}</div>
          ))}
          {data.no_accommodation.length === 0 && <p className="muted">Everyone has accommodation arranged.</p>}
        </div>
      </div>

      <div className="two-col">
        <div className="panel">
          <h3>Onsite today — dietary requirements ({data.dietary_today.length})</h3>
          {data.dietary_today.map((d: any, i: number) => (
            <div key={i} style={{ fontSize: 13, marginBottom: 3 }}>
              {d.name} — {d.requirement} {d.is_allergy && <span className="chip chip-allergy">allergy</span>}
            </div>
          ))}
          {data.dietary_today.length === 0 && <p className="muted">No dietary requirements among people onsite today.</p>}
          <p className="muted"><a href="#/catering">Full catering summary →</a></p>
        </div>

        <div className="panel">
          <h3>Currently on leave ({data.on_leave.length})</h3>
          {data.on_leave.map((l: any, i: number) => (
            <div key={i} style={{ fontSize: 13, marginBottom: 3 }}>
              {l.name} — {l.leave_type}, {toAus(l.start_date)} to {toAus(l.end_date)}
            </div>
          ))}
          {data.on_leave.length === 0 && <p className="muted">Nobody is on approved leave today.</p>}
        </div>
      </div>

      <div className="panel">
        <h3>Recent accommodation booking changes (7 days)</h3>
        {data.recent_booking_changes.length === 0 && <p className="muted">No booking changes in the last week.</p>}
        {data.recent_booking_changes.slice(0, 10).map((c: any) => (
          <div key={c.id} style={{ fontSize: 12, marginBottom: 3 }}>
            <span className="muted">{c.changed_at}</span> — booking #{c.record_id} {c.action}
            {c.field ? <> ({c.field}: {c.old_value || '—'} → {c.new_value || '—'})</> : ''} by {c.changed_by || 'unknown'}
          </div>
        ))}
      </div>
    </div>
  );
}

function toAusEmphasis(iso: string) { return toAus(iso); }
